import { useEffect, useMemo, useState } from "react";
import { SEED_HOBBIES } from "../../../shared/generated/hobbies";
import { readKit, writeKit, type UserKit } from "../lib/kv";
import { HERMES_ASSISTANT_KEY } from "../lib/assistant-chrome";
import { useAssistantChrome } from "../hooks/use-assistant-chrome";

const GEAR = [
  "bicicleta",
  "patinete",
  "guitarra",
  "tablero de ajedrez",
  "cuerda / slackline",
  "cometa",
  "puzles",
  "material de pintura",
  "zapatillas de correr",
  "disfraz",
  "libros",
  "cámara",
  "kayak",
  "coche",
  "moto",
  "abono transporte",
  "esterilla de yoga",
  "mochila de día",
];

export default function Kit() {
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<UserKit>({ hobbies: [], gear: [] });
  const [saved, setSaved] = useState<UserKit>({ hobbies: [], gear: [] });
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const { enabled: assistantEnabled, setEnabled: setAssistantEnabled, setMinimized: setAssistantMinimized } =
    useAssistantChrome(HERMES_ASSISTANT_KEY);

  useEffect(() => {
    void readKit().then((kit) => {
      setDraft(kit);
      setSaved(kit);
    });
  }, []);

  const dirty =
    draft.hobbies.join("|") !== saved.hobbies.join("|") ||
    draft.gear.join("|") !== saved.gear.join("|");

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SEED_HOBBIES.filter((h) => !q || h.label.toLowerCase().includes(q)).slice(0, 100);
  }, [query]);

  const toggleHobby = (label: string) => {
    setDraft((prev) => ({
      ...prev,
      hobbies: prev.hobbies.includes(label)
        ? prev.hobbies.filter((h) => h !== label)
        : [...prev.hobbies, label],
    }));
    setStatus("");
  };

  const toggleGear = (item: string) => {
    setDraft((prev) => ({
      ...prev,
      gear: prev.gear.includes(item) ? prev.gear.filter((g) => g !== item) : [...prev.gear, item],
    }));
    setStatus("");
  };

  const save = async () => {
    setBusy(true);
    try {
      await writeKit(draft);
      setSaved(draft);
      setStatus("Perfil guardado.");
    } catch {
      setStatus("No se pudo guardar. Reintenta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-kit">
      <section className="kit-hero">
        <p className="eyebrow">TU PERFIL</p>
        <h1 className="section-title">
          Perfil <span className="brand-inline">HERMES</span>
        </h1>
        <p className="lead">
          Marca hobbies y material. Pulsa Guardar para que los planes usen lo que tienes de verdad.
        </p>
        <p className="hero-meta">
          {draft.hobbies.length} hobbies · {draft.gear.length} objetos
          {dirty ? " · cambios sin guardar" : ""}
        </p>
      </section>

      <label className="field kit-search">
        Buscar hobby
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ajedrez, cometa, bici…"
        />
      </label>

      <div className="chip-cloud">
        {shown.map((hobby) => {
          const on = draft.hobbies.includes(hobby.label);
          return (
            <button
              key={hobby.id}
              type="button"
              className={`kit-chip${on ? " on" : ""}`}
              onClick={() => toggleHobby(hobby.label)}
            >
              {hobby.label}
            </button>
          );
        })}
      </div>

      <h2 className="subhead">Material que tienes</h2>
      <div className="chip-cloud">
        {GEAR.map((item) => {
          const on = draft.gear.includes(item);
          return (
            <button
              key={item}
              type="button"
              className={`kit-chip${on ? " on" : ""}`}
              onClick={() => toggleGear(item)}
            >
              {item}
            </button>
          );
        })}
      </div>

      <div className="toggle-row" style={{ marginTop: "1.5rem" }}>
        <button type="button" className="btn-copper" onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? "Guardando…" : "Guardar perfil"}
        </button>
        {status ? <p className="hero-meta">{status}</p> : null}
      </div>

      <section style={{ marginTop: "2rem" }}>
        <h2 className="subhead">Asistente</h2>
        <p className="hero-meta">Solo en Planes tras iniciar sesión. Deslízalo a la derecha para minimizarlo.</p>
        <label style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginTop: "0.75rem" }}>
          <input
            checked={assistantEnabled}
            onChange={(event) => {
              setAssistantEnabled(event.target.checked);
              if (event.target.checked) setAssistantMinimized(false);
            }}
            type="checkbox"
          />
          <span>Mostrar botón del asistente</span>
        </label>
      </section>
    </div>
  );
}
